import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Prompt, useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskProjectTemplateSetting,
  fetchTaskProjectTemplateSettingDetail,
  fetchTaskProjectTemplateSettingNewDetail,
  updateTaskProjectTemplateSetting,
} from '../../../../states/containers/task/taskSlice'
import { LangValue } from '../../../../states/types/common'
import { RequestPatchTaskProjectTemplateSetting, RequestPostTaskProjectTemplateSetting } from '../../../../states/types/task'
import { replaceAndStrip } from '../../../../utils/string'
import { setPath } from '../../../../utils/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import AddColorModule from '../../../components/task/AddColorModule'
import AddLabelModal from '../../../components/task/AddLabelModal'
import AddTeamAndMemberModule from '../../../components/task/AddTeamAndMemberModule'
import SetLabelModule from '../../../components/task/SetLabelModule'
import { URL } from '../../../constants/url'
import COLOR_LIST from '../../../constants/taskColor'

interface TemplateDetail {
  id?: number
  name: string
  color?: string
  code?: string
  description?: string
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

interface ModalState {
  label_group_id: number | null
  label_group_name: string
  label_id?: number | null
  label_name?: string
  label_code?: string | null
  label_color?: string
  label_master_id?: number | null
  scheduled_time?: number // 想定工数(秒)
  type: string
}

type LocationState = {
  keep?: boolean;
  labelGroupId?: number;
  detail?: TemplateDetail | null;
}

const Detail: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const {
    task,
  } = useSelector((state: RootState) => state)

  const [inputGroupName, setInputGroupName] = useState<string>("")
  const [inputGroupCode, setInputGroupCode] = useState<string>("")
  const [inputColor, setInputColor] = useState<string>("#f2a2a2")
  const [inputDescription, setInputDescription] = useState<string>("")
  // eslint-disable-next-line max-len
  const [selectUseTeams, setSelectUseTeams] = useState<{ id: number, origin_name: string, name: string, members: { id: number, name: string }[] }[]>([])
  const [selectUseMembers, setSelectUseMembers] = useState<{ id: number, name: string }[]>([])
  const [selectViewTeams, setSelectViewTeams] = useState<{ id: number, name: string, members: { id: number, name: string }[] }[]>([])
  const [selectViewMembers, setSelectViewMembers] = useState<{ id: number, name: string }[]>([])
  const [inputLabels, setInputLabels] = useState<ModalState[]>([])
  const [labelsError, setLabelsError] = useState<string>("")

  const [isDisplayAddLabel, setIsDisplayAddLabel] = useState<boolean>(false)

  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [isSubmit, setIsSubmit] = useState<boolean>(false)

  const locationState = history.location.state as LocationState

  const modalStateInit = {
    label_group_id: null,
    label_group_name: "",
    label_name: "",
    type: "create",
  }

  const [modalState, setModalState] = useState<ModalState>(modalStateInit)

  const templateDetail = useMemo(() => {
    if (!id && !task) return undefined
    return task.taskProjectTemplateSettingDetail?.label_group_template
  }, [id, task])

  const title = useMemo(() => {
    if (id) {
      return "ユーザーテンプレート編集"
    }
    return "新規ユーザーテンプレート作成"
  }, [id, locationState])

  const description = useMemo(() => {
    if (id) {
      return (
        <>
          <p>
            ユーザーテンプレートを編集することができます。
          </p>
        </>
      )
    }
    return (
      <>
        <p>
          ユーザーテンプレートを作成し、プロジェクト作成時に使用することができます。
        </p>
      </>
    )
  }, [id, locationState])

  const tasks = useMemo(() => (
    [...task.taskProjectTemplateSettingDetail?.label_masters.filter((v) => !inputLabels.map((w) => w.label_name).includes(v.name)) || []]
  ), [task, inputLabels])

  const randomInt = (min: number, max: number) => {
    const intMin = Math.ceil(min)
    const intMax = Math.floor(max)
    return Math.floor(Math.random() * (intMax - intMin) + intMin)
  }

  const randomColor = () => COLOR_LIST.map((v) => v.values).flat()[randomInt(0, 23)].color

  const addLabelGroup = () => {
    setIsSubmit(true)
    setIsEdit(false)
    if (!id) {
      const tmpVal: RequestPostTaskProjectTemplateSetting = {
        name: inputGroupName,
        code: inputGroupCode || null,
        color: inputColor || "",
        description: inputDescription || "",
        permission: {
          viewable: {
            team_ids: selectViewTeams.map((v) => v.id) || [],
            member_ids: selectViewMembers.map((v) => v.id) || [],
          },
          usable: {
            team_ids: selectUseTeams.map((v) => v.id) || [],
            member_ids: selectUseMembers.map((v) => v.id) || [],
          },
        },
        labels: inputLabels.filter((v) => v.label_name).map((v) => ({
          label_master_id: v.label_master_id || null,
          name: v.label_name || "",
          code: v.label_code || null,
          color: v.label_color || randomColor(),
        })) || [],
      }
      dispatch(
        createTaskProjectTemplateSetting(
          tmpVal,
          () => {
            alert.success("ユーザーテンプレートを作成しました！")
            history.push(`${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}`)
          },
        ),
      )
    } else {
      const tmpVal: RequestPatchTaskProjectTemplateSetting = { id: id }
      if (templateDetail?.name !== inputGroupName) {
        tmpVal.name = inputGroupName
      }
      if (templateDetail?.code !== inputGroupCode) {
        tmpVal.code = inputGroupCode || null
      }
      if (inputColor && templateDetail?.color !== inputColor) {
        tmpVal.color = inputColor
      }
      if (inputColor && templateDetail?.description !== inputDescription) {
        tmpVal.description = inputDescription.replace(/\n\n/g, "\n")
      }
      tmpVal.permission = {
        viewable: {
          team_ids: selectViewTeams.map((v) => v.id) || [],
          member_ids: selectViewMembers.map((v) => v.id) || [],
        },
        usable: {
          team_ids: selectUseTeams.map((v) => v.id) || [],
          member_ids: selectUseMembers.map((v) => v.id) || [],
        },
      }
      tmpVal.labels = inputLabels.filter((v) => v.label_name).map((v) => ({
        label_id: v.label_id || null,
        label_master_id: v.label_master_id || 0,
        name: v.label_name || "",
        code: v.label_code || null,
        color: v.label_color || randomColor(),
      }))
      dispatch(
        updateTaskProjectTemplateSetting(
          tmpVal,
          () => {
            alert.success("ユーザーテンプレートを編集しました！")
            if (locationState?.keep) {
              history.goBack()
            } else {
              history.push(`${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}`)
            }
          },
        ),
      )
    }
  }

  const addLabel = (params: ModalState) => {
    setIsEdit(true)
    if (params.type === "create") {
      setInputLabels([...inputLabels, params])
    } else {
      setInputLabels(inputLabels.map((v) => (v.label_name === params.label_name ? params : v)))
    }
    setIsDisplayAddLabel(false)
  }

  const closeAddLabel = () => {
    setIsDisplayAddLabel(false)
    setModalState(modalStateInit)
  }

  const changeViewTeam = (teams: { id: number, name: string, members: { id: number, name: string }[] }[]) => {
    setIsEdit(true)
    setSelectViewTeams(teams)
  }

  const changeViewMember = (members: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectViewMembers(members)
  }

  const changeUseTeam = (teams: { id: number, origin_name: string, name: string, members: { id: number, name: string }[] }[]) => {
    setIsEdit(true)
    setSelectUseTeams(teams)
    const addDiffTeams = teams.filter((v) => !selectUseTeams.map((w) => w.id).includes(v.id))
      .filter((v) => !selectViewTeams.map((w) => w.id).includes(v.id))
    if (!addDiffTeams) return
    setSelectViewTeams(
      [
        ...selectViewTeams,
        ...addDiffTeams.map((v) => ({
          id: v.id,
          name: `チーム「${v.origin_name}」のマネージャー`,
          members: task.taskProjectTemplateSettingDetail?.teams.find((w) => w.id === v.id)?.managers || [],
        })),
      ],
    )
  }

  const changeUseMember = (members: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectUseMembers(members)
  }

  const changeInputColor = (value: string) => {
    setIsEdit(true)
    setInputColor(value)
  }

  const changeInputGroupName = (value: string) => {
    setIsEdit(true)
    setInputGroupName(value)
  }

  const changeInputDescription = (value: string) => {
    setIsEdit(true)
    setInputDescription(value)
  }

  const existsSameValue = (arr: string[]) => {
    const tmpArr = new Set(arr)
    return tmpArr.size !== arr.length
  }

  const addLabelList = () => {
    setIsEdit(true)
    setInputLabels([...inputLabels, modalStateInit])
  }

  const delLabelList = (num: number) => {
    setIsEdit(true)
    setInputLabels([...inputLabels.filter((_v, i) => i !== num)])
  }

  const selectLabel = (labelName: string, num: number) => {
    setIsEdit(true)
    const param = tasks.find((v) => v.name === labelName)
    if (!param) {
      setInputLabels(
        [...inputLabels.map((v, i) => (i === num ? {
          ...modalStateInit,
          label_name: labelName,
        } : v))],
      )
      return
    }
    setInputLabels(
      [...inputLabels.map((v, i) => (i === num ? {
        ...modalStateInit,
        label_name: param.name,
        label_master_id: param.id,
        label_color: param.color,
      } : v))],
    )
  }

  const handleBeforeUnloadEvent = (event: BeforeUnloadEvent): void => {
    event.preventDefault()
    event.returnValue = "check"
  }

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnloadEvent)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnloadEvent)
    }
  }, [])

  useEffect(() => {
    setInputGroupName(templateDetail?.name || "")
    setInputGroupCode(templateDetail?.code || "")
    setInputColor(templateDetail?.color || randomColor())
    setInputDescription(templateDetail?.description || "")
    setSelectUseTeams(templateDetail?.permission.usable.teams.map((v) => (
      {
        ...v,
        origin_name: v.name,
        members: task.taskProjectTemplateSettingDetail?.teams.find((w) => w.id === v.id)?.members || [],
      }
    )) || [])
    setSelectUseMembers(templateDetail?.permission.usable.members || [])
    setSelectViewTeams(templateDetail?.permission.viewable.teams.map((v) => (
      {
        id: v.id,
        name: `チーム「${v.name}」のマネージャー`,
        members: task.taskProjectTemplateSettingDetail?.teams.find((w) => w.id === v.id)?.managers || [],
      }
    )) || [])
    setSelectViewMembers(templateDetail?.permission.viewable.members || [])
    setInputLabels(
      templateDetail?.labels.map((v) => (
        {
          label_group_id: templateDetail.id,
          label_group_name: templateDetail.name,
          label_id: v.id,
          label_name: v.name,
          label_code: v.code,
          label_color: v.color,
          label_master_id: v.label_master_id,
          type: "update",
        }
      )) || [],
    )
  }, [templateDetail])

  useEffect(() => {
    if (existsSameValue(inputLabels.filter((v) => v.label_name).map((v) => v.label_name || ""))) {
      setLabelsError("業務が重複しています。異なる業務を設定してください。")
    } else {
      setLabelsError("")
    }
  }, [inputLabels])

  useEffect(() => {
    if (id) {
      dispatch(fetchTaskProjectTemplateSettingDetail({ id: id }))
    } else {
      dispatch(fetchTaskProjectTemplateSettingNewDetail())
    }
  }, [id, locationState])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  return (
    <section className="task_page task_status">
      <Helmet>
        <title>{`Qasee - 工数管理 - 設定  - その他の設定 - プロジェクト設定 - ${title}`}</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        その他の設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.TASK_PROJECT_TEMPLATE_SETTING.path, language)}
        >
          ユーザーテンプレート設定
        </Link>
        &nbsp;&gt;&nbsp;
        {title}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {title}
        </h1>
      </section>
      <section className="page_description">
        {description}
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    ユーザーテンプレートを読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div className="project_input_area">
                    <fieldset>
                      <legend>
                        ユーザーテンプレート概要
                      </legend>
                      <div className="item w20">
                        <div className="title">
                          プロジェクトカラー
                        </div>
                        <div className="detail equal_space row_1">
                          <AddColorModule
                            color={inputColor}
                            changeCallback={(color) => changeInputColor(color)}
                          />
                        </div>
                      </div>
                      <div className="item w80">
                        <div className="title">
                          テンプレート名
                          <div className="required">必須</div>
                        </div>
                        <div className="detail equal_space row_1">
                          <input
                            type="text"
                            value={inputGroupName}
                            onChange={(e) => changeInputGroupName(e.target.value)}
                            onBlur={() => changeInputGroupName(replaceAndStrip(inputGroupName))}
                            placeholder="プロジェクト名を入力"
                          />
                        </div>
                        {
                          !inputGroupName.length && (
                            <div className="error">
                              テンプレート名を入力してください
                            </div>
                          )
                        }
                      </div>
                      <div className="item w100">
                        <div className="title">
                          説明
                        </div>
                        <div className="detail equal_space row_1">
                          <textarea
                            style={{
                              height: "auto",
                              width: "100%",
                              padding: "16px",
                              backgroundColor: "#FFFFFF",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              fontSize: "14px",
                              minHeight: "7em",
                            }}
                            value={inputDescription}
                            onChange={(e) => changeInputDescription(e.target.value)}
                          />
                        </div>
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          プロジェクト表示チーム・メンバー
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail equal_space row_1">
                          <div className="team_area">
                            {
                              selectViewTeams.map((team) => (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "8px",
                                      borderBottom: "solid 1px #CCCCCC",
                                      backgroundColor: "#EEEEEE",
                                    }}
                                  >
                                    <div>
                                      {team.name}
                                    </div>
                                    <div>
                                      <span
                                        className="delete"
                                        onClick={() => changeViewTeam([...selectViewTeams.filter((v) => v.id !== team.id)])}
                                        role="none"
                                      >
                                        <FontAwesomeIcon icon={['fas', 'times']} />
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className="chip_area"
                                    style={{
                                      padding: "8px",
                                    }}
                                  >
                                    {
                                      team.members.map((member) => (
                                        <div
                                          className="chip"
                                          key={`member_${member.id}`}
                                        >
                                          {member.name}
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              ))
                            }
                            <div className="chip_area">
                              {
                                selectViewMembers.map((member) => (
                                  <div
                                    className="chip"
                                    key={`member_${member.id}`}
                                  >
                                    {member.name}
                                    <span
                                      className="delete"
                                      onClick={() => changeViewMember([...selectViewMembers.filter((v) => v.id !== member.id)])}
                                      role="none"
                                    >
                                      <FontAwesomeIcon icon={['fas', 'times']} />
                                    </span>
                                  </div>
                                ))
                              }
                            </div>
                            <div className="chip_area">
                              <AddTeamAndMemberModule
                                teamIdList={selectViewTeams.map((v) => v.id)}
                                callbackTeams={(teams) => changeViewTeam(teams)}
                                teamList={
                                  task.taskProjectTemplateSettingDetail?.teams.map((v) => (
                                    { ...v, origin_name: v.name, name: `チーム「${v.name}」のマネージャー`, members: v.managers }
                                  )) || []
                                }
                                memberIdList={selectViewMembers.map((v) => v.id)}
                                callbackMembers={(members) => changeViewMember(members)}
                                memberList={
                                  task.taskProjectTemplateSettingDetail?.members.map((v) => ({ id: v.id, name: v.name })) || []
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          プロジェクト参加チーム・メンバー
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail equal_space row_1">
                          <div className="team_area">
                            {
                              selectUseTeams.map((team) => (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "8px",
                                      borderBottom: "solid 1px #CCCCCC",
                                      backgroundColor: "#EEEEEE",
                                    }}
                                  >
                                    <div>
                                      {team.name}
                                    </div>
                                    <div>
                                      <span
                                        className="delete"
                                        onClick={() => changeUseTeam([...selectUseTeams.filter((v) => v.id !== team.id)])}
                                        role="none"
                                      >
                                        <FontAwesomeIcon icon={['fas', 'times']} />
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className="chip_area"
                                    style={{
                                      padding: "8px",
                                    }}
                                  >
                                    {
                                      team.members.map((member) => (
                                        <div
                                          className="chip"
                                          key={`member_${member.id}`}
                                        >
                                          {member.name}
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              ))
                            }
                            <div className="chip_area">
                              {
                                selectUseMembers.map((member) => (
                                  <div
                                    className="chip"
                                    key={`member_${member.id}`}
                                  >
                                    {member.name}
                                    <span
                                      className="delete"
                                      onClick={() => changeUseMember([...selectUseMembers.filter((v) => v.id !== member.id)])}
                                      role="none"
                                    >
                                      <FontAwesomeIcon icon={['fas', 'times']} />
                                    </span>
                                  </div>
                                ))
                              }
                            </div>
                            <div className="chip_area">
                              <AddTeamAndMemberModule
                                teamIdList={selectUseTeams.map((v) => v.id)}
                                callbackTeams={(teams) => changeUseTeam(teams)}
                                teamList={
                                  task.taskProjectTemplateSettingDetail?.teams.map((v) => (
                                    { ...v, origin_name: v.name, name: `チーム「${v.name}」のメンバー` }
                                  )) || []
                                }
                                memberIdList={selectUseMembers.map((v) => v.id)}
                                callbackMembers={(members) => changeUseMember(members)}
                                memberList={
                                  task.taskProjectTemplateSettingDetail?.members.map((v) => ({ id: v.id, name: v.name })) || []
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          プロジェクト業務
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail row_1">
                          {
                            inputLabels.map((inputLabel, i) => (
                              <React.Fragment key={`job_${i.toString()}`}>
                                <div className="label_area">
                                  <SetLabelModule
                                    currentLabelName={inputLabel.label_name || null}
                                    callbackLabel={(labelName) => selectLabel(labelName, i)}
                                    labelList={tasks}
                                    existLabel={!!inputLabel.label_master_id}
                                  />
                                  <Tips
                                    description="業務を削除"
                                    className="del"
                                    icon={false}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => delLabelList(i)}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                    </button>
                                  </Tips>
                                </div>
                              </React.Fragment>
                            ))
                          }
                          {
                            labelsError && (
                              <div
                                style={{
                                  color: "#FF0000",
                                  margin: "4px 0",
                                }}
                              >
                                {labelsError}
                              </div>
                            )
                          }
                          <div className="add_area">
                            <button
                              type="button"
                              onClick={addLabelList}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              &nbsp;
                              業務追加
                            </button>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "8px",
                        gap: "8px",
                      }}
                    >
                      <button
                        type="button"
                        className="button cancel"
                        onClick={() => history.go(-1)}
                      >
                        キャンセル
                      </button>
                      <button
                        type="button"
                        className="button"
                        onClick={addLabelGroup}
                        disabled={!inputGroupName || isSubmit}
                      >
                        保存
                      </button>
                    </div>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      {/* 作業作成モーダル */}
      <AddLabelModal
        modalState={modalState}
        closeCallback={closeAddLabel}
        addCallback={(params) => addLabel(params)}
        isDisplay={isDisplayAddLabel}
        tasks={[...task.taskProjectTemplateSettingDetail?.label_masters.filter((v) => !inputLabels.map((w) => w.label_name).includes(v.name)) || []]}
        currentTasks={inputLabels.map((v) => v.label_name || "")}
        teamList={selectUseTeams}
        memberList={selectUseMembers}
      />
      <Prompt
        when={isEdit}
        message="このページから移動しますか？行った変更が保存されない可能性があります。"
      />
    </section>
  )
}

export default Detail
